import { useEffect, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { goApi } from '../../../services';
import {
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    Box,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { CopyIcon, EditIcon } from 'lucide-react';
import { ChatBubble, Visibility, Delete, PlayCircle, YouTube, LiveTv } from '@mui/icons-material';
import { isScheduleLive } from '../../../class-utilities';
import { apiRoutes } from '../../../services/apiRoutes';
import { LoadingSection } from '../../../common/LoadingSection';
import { ScheduleForm } from './ScheduleForm';
import moment from 'moment';
import { DuplicateScheduleForm } from './DublicateScheduleForm';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import VideoPlayerPage from '../../../common/VideoPlayer/VideoPlayerPage';
import CloseIcon from '@mui/icons-material/Close';
const LiveChatItem = ({ templateId, record }: any) => {
    const navigate = useNavigate();
    if ((record.videoId || record?.streamKey) && isScheduleLive(record as any)) {
        return (
            <Button
                onClick={() =>
                    navigate(`/batch_schedules/${record?.id}/chat?templateId=${templateId}`)
                }
                startIcon={<ChatBubble />}
                size="small"
            >
                Chat
            </Button>
        );
    }
    return null;
};

const ScheduleEditButton = ({ templateId, record, onRefresh }: any) => {
    const [openModal, setOpenModal] = useState(false);
    return (
        <>
            <Button onClick={() => setOpenModal(true)} size="small">
                <EditIcon size={18} />
            </Button>
            <Dialog
                open={openModal}
                title={'Schedule Form'}
                onClose={(_refresh: boolean) => {
                    setOpenModal(false);
                }}
            >
                <ScheduleForm
                    batchId={record?.batchId}
                    templateId={templateId}
                    schedule={record}
                    onClose={(_refresh: boolean) => {
                        if (_refresh && onRefresh) {
                            onRefresh();
                        }
                        setOpenModal(false);
                    }}
                />
            </Dialog>
        </>
    );
};

const ScheduleDuplicateButton = ({ templateId, record }: any) => {
    const [openModal, setOpenModal] = useState(false);
    const refresh = useRefresh();
    return (
        <>
            <Button onClick={() => setOpenModal(true)}>
                <CopyIcon />
            </Button>
            <Dialog
                open={openModal}
                title={'Schedule Form'}
                onClose={(_refresh: boolean) => {
                    setOpenModal(false);
                }}
            >
                <DuplicateScheduleForm
                    batchId={record?.batchId}
                    templateId={templateId}
                    schedule={record}
                    onClose={(_refresh: boolean) => {
                        if (_refresh) {
                            refresh();
                        }
                        setOpenModal(false);
                    }}
                />
            </Dialog>
        </>
    );
};

const ScheduleShowButton = ({ templateId, record }: any) => {
    const navigate = useNavigate();
    return (
        <Button
            onClick={() => navigate(`/batch_schedules/${record?.id}/show?templateId=${templateId}`)}
            size="small"
        >
            <Visibility />
        </Button>
    );
};

const ScheduleDeleteButton = ({ record, onRefresh }: any) => {
    const notify = useNotify();
    const [deleting, setDeleting] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const handleDelete = async () => {
        if (!record?.id) return;
        setOpenDialog(false);
        setDeleting(true);
        try {
            await goApi.delete(`${apiRoutes.BATCH_SCHEDULE}/${record.id}`);
            notify('Schedule deleted successfully', { type: 'success' });
            if (onRefresh) onRefresh();
        } catch (err: any) {
            notify(err?.message || 'Failed to delete schedule', { type: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Button
                onClick={() => setOpenDialog(true)}
                disabled={deleting}
                color="error"
                size="small"
            >
                <Delete />
            </Button>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Delete Schedule</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete schedule "{record?.title}"? This action
                        cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error" disabled={deleting} autoFocus>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const ScheduleTabularViewAction = ({ templateId, record, onRefresh, setOpenVideoModal }: any) => {
    return (
        <div className={'flex space-x-2'}>
            <LiveChatItem templateId={templateId} record={record} />
            <ScheduleDeleteButton record={record} onRefresh={onRefresh} />
            <ScheduleShowButton templateId={templateId} record={record} />
            <ScheduleEditButton templateId={templateId} record={record} onRefresh={onRefresh} />
            <ScheduleDuplicateButton templateId={templateId} record={record} />
            <PlayCircleIcon
                className="text-blue-600"
                onClick={() => {
                    setOpenVideoModal(record);
                }}
            />
        </div>
    );
};

const PlayerIcon = ({ record }: any) => {
    const isYoutube = record?.video?.type === 'YOUTUBE';
    const isLiveStream = record?.streamKey;
    return (
        <>
            {isYoutube ? (
                <YouTube style={{ width: 24, height: 24, color: '#FF0000' }} />
            ) : isLiveStream ? (
                <LiveTv style={{ width: 24, height: 24, color: '#555' }} />
            ) : (
                <PlayCircle style={{ width: 24, height: 24, color: '#555' }} />
            )}
        </>
    );
};

export const BatchScheduleTabular = ({ batch, refreshKey }: { batch: any; refreshKey?: any }) => {
    const [subjects, setSubjects] = useState<any[]>([]);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [schedules, setSchedules] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [orderBy, setOrderBy] = useState<'startTime' | 'endTime'>('startTime');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const templateId = batch?.templateId;
    const notify = useNotify();
    const [openVideoModal, setOpenVideoModal] = useState<any>(null);
    useEffect(() => {
        const fetchSubjects = async () => {
            if (!templateId) return;
            setLoadingSubjects(true);
            try {
                const response = await goApi.get(
                    `${apiRoutes.BATCH_TEMPLATE_SUBJECTS}?batchTemplateId=${templateId}&limit=50`,
                );
                setSubjects(response.data?.items || []);
            } catch (err) {
                console.error('Failed to fetch subjects', { type: 'error' });
            } finally {
                setLoadingSubjects(false);
            }
        };
        fetchSubjects();
    }, [templateId]);

    const fetchSchedules = async () => {
        if (!batch?.id || !templateId) return;
        setLoading(true);
        try {
            const response = await goApi.get(
                `/batches/schedules/${
                    batch.id
                }/all-schedule?page=${page}&limit=${limit}&batchTemplateId=${templateId}&orderBy=${orderBy}&order=${order.toUpperCase()}`,
            );
            setSchedules(response.data?.items || []);
            setTotal(response.data?.pagination?.total || 0);
        } catch (err: any) {
            notify(err?.message || 'Failed to fetch schedules', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchedules();
    }, [batch?.id, orderBy, order, page, limit, refreshKey]);

    const handleSort = (property: 'startTime' | 'endTime') => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(1); // Reset to first page on sort
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleLimitChange = (event: any) => {
        setLimit(event.target.value);
        setPage(1); // Reset to first page on limit change
    };

    const formatDateTime = (dateTime: string) => {
        return moment(dateTime).format('DD-MMM-YYYY hh:mm A');
    };

    return (
        <>
            <Alert severity="warning" sx={{ mb: 2 }}>
                * Faded schedules means their subjects are removed from batch template. Please check
                this may cause inconsistency on students side. Either remove this schedule or add
                subject to batch template
            </Alert>
            {loadingSubjects || loading ? (
                <LoadingSection />
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead sx={{ backgroundColor: '#fafafa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Flags</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                                    <TableSortLabel
                                        active={orderBy === 'startTime'}
                                        direction={orderBy === 'startTime' ? order : 'asc'}
                                        onClick={() => handleSort('startTime')}
                                    >
                                        Start / End time
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                                    Created at
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedules.map(schedule => {
                                const isSubjectPresent = subjects?.find(
                                    s => s.subjectId === schedule.subjectId,
                                );
                                return (
                                    <TableRow
                                        key={schedule.id}
                                        sx={{
                                            opacity: isSubjectPresent ? 1 : 0.4,
                                            '&:hover': { backgroundColor: '#fafafa' },
                                        }}
                                    >
                                        <TableCell>
                                            <PlayerIcon record={schedule} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {schedule.title}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                {schedule.isDemo && (
                                                    <Chip
                                                        label="Demo"
                                                        color="success"
                                                        size="small"
                                                        sx={{ height: 24 }}
                                                    />
                                                )}

                                                {schedule.chatEnabled && (
                                                    <Chip
                                                        label="Chat"
                                                        color="secondary"
                                                        size="small"
                                                        sx={{ height: 24 }}
                                                    />
                                                )}

                                                {schedule.parentScheduleId && (
                                                    <Chip
                                                        label="Multi"
                                                        color="info"
                                                        size="small"
                                                        sx={{ height: 24 }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Chip
                                                        label="S"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#c8e6c9',
                                                            color: '#000',
                                                            height: 20,
                                                            minWidth: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': {
                                                                padding: '0 6px',
                                                            },
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 13 }}>
                                                        {formatDateTime(schedule.startTime)}
                                                    </span>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Chip
                                                        label="E"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#ffcdd2',
                                                            color: '#000',
                                                            height: 20,
                                                            minWidth: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': {
                                                                padding: '0 6px',
                                                            },
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 13 }}>
                                                        {formatDateTime(schedule.endTime)}
                                                    </span>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Chip
                                                        label="C"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#fff9c4',
                                                            color: '#000',
                                                            height: 20,
                                                            minWidth: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': {
                                                                padding: '0 6px',
                                                            },
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 13 }}>
                                                        {formatDateTime(schedule.createdAt)}
                                                    </span>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Chip
                                                        label="U"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#e1bee7',
                                                            color: '#000',
                                                            height: 20,
                                                            minWidth: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': {
                                                                padding: '0 6px',
                                                            },
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 13 }}>
                                                        {formatDateTime(schedule.updatedAt)}
                                                    </span>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <ScheduleTabularViewAction
                                                templateId={templateId}
                                                record={schedule}
                                                onRefresh={fetchSchedules}
                                                setOpenVideoModal={setOpenVideoModal}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {schedules.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        No schedules found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 2,
                            borderTop: '1px solid #e0e0e0',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: 14 }}>Rows per page:</span>
                            <FormControl size="small">
                                <Select value={limit} onChange={handleLimitChange}>
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>
                            <span style={{ fontSize: 14, marginLeft: 16 }}>
                                {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
                            </span>
                        </Box>
                        <Pagination
                            count={Math.ceil(total / limit)}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </TableContainer>
            )}

            <Dialog
                open={!!openVideoModal}
                onClose={() => {
                    setOpenVideoModal(null);
                }}
                maxWidth="lg"
                fullWidth
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenVideoModal(null)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1000,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <VideoPlayerPage
                        url={openVideoModal?.video?.processedUrl}
                        type={openVideoModal?.video?.type}
                        streamKey={openVideoModal?.streamKey}
                    />
                </Box>
            </Dialog>
        </>
    );
};
